
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
// Image removed - use img tag;
import { Link } from "react-router-dom";
import {
  Heart,
  ShoppingBag,
  Share2,
  Facebook,
  Twitter,
  Image as ImageIcon,
  Minus,
  Plus,
  Truck,
  Shield,
  RotateCcw,
  Star,
  Check,
  ArrowLeft,
} from "lucide-react";
import { useCartStore, useWishlistStore, useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import { productsApi } from "@/lib/api";
import { getSmartProductPlaceholder } from "@/lib/placeholders";
import OTPLoginDialog from "@/components/auth/OTPLoginDialog";

interface ProductImage {
  id: number;
  image_url: string;
  alt_text: string | null;
  is_primary: boolean;
  display_order: number;
}

interface Product {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  price: number;
  original_price: number | null;
  image_url: string | null;
  stock: number;
  order_count: number;
  collection_id: number | null;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  images?: ProductImage[];
}

export default function ProductDetailPage() {
  const params = useParams();
  const slug = params.slug as string;
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<"description" | "dimensions" | "care" | "shipping">("description");
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  const { addItem: addToCart } = useCartStore();
  const { addItem: addToWishlist, isInWishlist, removeItem: removeFromWishlist, addItemToBackend, removeItemFromBackend } = useWishlistStore();
  const { isAuthenticated, token } = useAuthStore();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await productsApi.getBySlug(slug);
        setProduct(data);
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchProduct();
    }
  }, [slug]);

  const handleAddToCart = () => {
    if (!product) return;
    addToCart({
      id: `${product.id}`,
      productId: product.id,
      name: product.name,
      price: product.price,
      image: product.image_url || "/product1.png",
      quantity,
    });
    toast.success("Added to cart!");
  };

  const handleWishlistToggle = async () => {
    if (!product) return;
    
    // Check if user is authenticated
    if (!isAuthenticated || !token) {
      setShowLoginDialog(true);
      return;
    }
    
    try {
      if (isInWishlist(product.id)) {
        // Remove from wishlist
        const success = await removeItemFromBackend(product.id, token);
        if (success) {
          removeFromWishlist(product.id);
          toast.success("Removed from wishlist");
        } else {
          toast.error("Failed to remove from wishlist");
        }
      } else {
        // Add to wishlist
        const success = await addItemToBackend(product.id, token);
        if (success) {
          addToWishlist({
            id: product.id,
            name: product.name,
            price: product.price,
            originalPrice: product.original_price || undefined,
            image: product.image_url || getSmartProductPlaceholder(product.name),
            slug: product.slug,
          });
          toast.success("Added to wishlist!");
        } else {
          toast.error("Failed to add to wishlist");
        }
      }
    } catch (error) {
      console.error("Wishlist toggle error:", error);
      toast.error("An error occurred. Please try again.");
    }
  };

  const handleShare = (platform: string) => {
    if (!product) return;
    const url = window.location.href;
    const text = product.name;
    const imageUrl = product.image_url || "";
    let shareUrl = "";

    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
        break;
      case "pinterest":
        shareUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(imageUrl)}&description=${encodeURIComponent(text)}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank", "width=600,height=400");
    }
  };

  // Calculate discount
  const calculateDiscount = (price: number, originalPrice: number | null) => {
    if (!originalPrice || originalPrice <= price) return 0;
    return Math.round(((originalPrice - price) / originalPrice) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <main className="flex-grow pt-24 pb-24">
          <div className="container mx-auto px-4 py-12">
            {/* Breadcrumb Skeleton */}
            <div className="mb-6">
              <div className="h-4 w-48 bg-gray-200 rounded animate-pulse" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Image Gallery Skeleton */}
              <div>
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-200 animate-pulse mb-4" />
                <div className="grid grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="relative aspect-square rounded-lg bg-gray-200 animate-pulse" />
                  ))}
                </div>
              </div>

              {/* Product Info Skeleton */}
              <div className="space-y-6">
                <div className="h-10 w-3/4 bg-gray-200 rounded animate-pulse" />
                <div className="h-8 w-1/2 bg-gray-200 rounded animate-pulse" />
                <div className="h-6 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-6 w-2/3 bg-gray-200 rounded animate-pulse" />
                <div className="h-12 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-12 w-full bg-gray-200 rounded animate-pulse" />
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-5 w-full bg-gray-200 rounded animate-pulse" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <main className="flex-grow pt-24 pb-24">
          <div className="container mx-auto px-4 py-12 text-center">
            <h1 className="text-3xl font-bold mb-4">Product Not Found</h1>
            <p className="text-gray-600 mb-6">The product you're looking for doesn't exist.</p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Products</span>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const discount = calculateDiscount(product.price, product.original_price);
  const isOutOfStock = product.stock === 0;
  
  // Get all product images, sorted by display_order, with primary first
  const productImages = product.images && product.images.length > 0
    ? [...product.images].sort((a, b) => {
        // Primary image first, then by display_order
        if (a.is_primary && !b.is_primary) return -1;
        if (!a.is_primary && b.is_primary) return 1;
        return a.display_order - b.display_order;
      })
    : [];
  
  // Fallback to image_url if no images exist
  const allImages = productImages.length > 0
    ? productImages.map(img => img.image_url)
    : [product.image_url || getSmartProductPlaceholder(product.name)];
  
  const currentImage = allImages[selectedImageIndex] || allImages[0];

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow pt-24 pb-24">
        <div className="container mx-auto px-4 py-12">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Products</span>
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery with Carousel */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Main Image Carousel */}
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-100 mb-4 shadow-lg">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={selectedImageIndex}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="absolute inset-0"
                  >
                    <img
                      src={currentImage}
                      alt={productImages[selectedImageIndex]?.alt_text || product.name}
                      className="object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = getSmartProductPlaceholder(product.name);
                      }}
                    />
                  </motion.div>
                </AnimatePresence>
                
                {/* Navigation Arrows (if multiple images) */}
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setSelectedImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length)}
                      className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/80 hover:bg-white rounded-full shadow-md transition-all"
                      aria-label="Previous image"
                    >
                      <ArrowLeft className="w-4 h-4 text-gray-900" />
                    </button>
                    <button
                      onClick={() => setSelectedImageIndex((prev) => (prev + 1) % allImages.length)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2 bg-white/80 hover:bg-white rounded-full shadow-md transition-all rotate-180"
                      aria-label="Next image"
                    >
                      <ArrowLeft className="w-4 h-4 text-gray-900" />
                    </button>
                  </>
                )}
                
                {/* Discount Badge */}
                {discount > 0 && (
                  <div className="absolute top-3 left-3 z-10">
                    <span className="px-2.5 py-1 bg-gray-900 text-white rounded text-xs font-medium">
                      {discount}% OFF
                    </span>
                  </div>
                )}
                {/* Stock Badge */}
                {product.stock > 0 && product.stock <= 5 && (
                  <div className="absolute top-3 right-3 z-10">
                    <span className="px-2.5 py-1 bg-gray-900 text-white rounded text-xs font-medium">
                      Only {product.stock} left
                    </span>
                  </div>
                )}
                {isOutOfStock && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10">
                    <span className="px-4 py-2 bg-gray-900 text-white rounded text-sm font-medium">
                      Out of Stock
                    </span>
                  </div>
                )}
              </div>
              
              {/* Thumbnail Navigation */}
              {allImages.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                  {allImages.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index
                          ? "border-gray-900"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img
                        src={img}
                        alt={`${product.name} ${index + 1}`}
                        
                        className="object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = getSmartProductPlaceholder(product.name);
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Product Name */}
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold mb-3 text-gray-900">
                  {product.name}
                </h1>
                {/* Order Count Badge */}
                {product.order_count > 0 && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full">
                    <Star className="w-3.5 h-3.5 text-gray-600 -gray-600" />
                    <span className="text-xs text-gray-600">
                      {product.order_count.toLocaleString()}+ Orders
                    </span>
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="space-y-2">
                {product.original_price && discount > 0 && (
                  <div className="text-sm text-gray-500">
                    <span className="line-through">Regular price ₹{product.original_price.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  {product.original_price && discount > 0 ? (
                    <span className="text-2xl md:text-3xl font-semibold text-gray-900">
                      Sale price ₹{product.price.toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-2xl md:text-3xl font-semibold text-gray-900">
                      ₹{product.price.toLocaleString()}
                    </span>
                  )}
                  {product.original_price && discount > 0 && (
                    <span className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                      Save {discount}%
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Tax included. Shipping calculated at checkout.</p>
              </div>

              {/* Stock Status */}
              {!isOutOfStock && product.stock > 0 && product.stock <= 10 && (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                    Low stock - Only {product.stock} items left
                  </p>
                </div>
              )}

              {/* Quantity Selector */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Quantity</label>
                <div className="flex items-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Minus className="w-4 h-4 text-gray-700" />
                  </motion.button>
                  <span className="text-lg font-medium w-12 text-center text-gray-900">{quantity}</span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setQuantity(Math.min(product.stock || 10, quantity + 1))}
                    disabled={quantity >= (product.stock || 10)}
                    className="p-2 rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4 text-gray-700" />
                  </motion.button>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2.5 border border-gray-200">
                <div className="flex items-center gap-2.5 text-xs text-gray-600">
                  <Truck className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span>Express 24-Hour Delivery in Mumbai (Charges Apply)</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-gray-600">
                  <Truck className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span>Free Shipping on Prepaid Orders Above ₹500</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-gray-600">
                  <Shield className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span>COD, Easy Returns & Quick Exchanges</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-gray-600">
                  <Check className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <span>Premium Quality Stones, Plating & Handwork</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-gray-600">
                  <Star className="w-4 h-4 text-gray-500 -gray-500 flex-shrink-0" />
                  <span>Trusted by 22K+ Customers Worldwide</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                >
                  <ShoppingBag className="w-4 h-4" />
                  <span>{isOutOfStock ? "Out of Stock" : "Add to Cart"}</span>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleWishlistToggle}
                  className={`p-3 rounded-lg border transition-all ${
                    isInWishlist(product.id)
                      ? "border-gray-400 bg-gray-100 text-gray-700"
                      : "border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-600"
                  }`}
                >
                  <Heart className={`w-4 h-4 ${isInWishlist(product.id) ? "-current" : ""}`} />
                </motion.button>
              </div>

              {/* Social Share */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Share</p>
                <div className="flex flex-wrap gap-2">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleShare("facebook")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all text-xs text-gray-600"
                    aria-label="Share on Facebook"
                  >
                    <Facebook className="w-3.5 h-3.5" />
                    <span>Share on Facebook</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleShare("twitter")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all text-xs text-gray-600"
                    aria-label="Share on Twitter"
                  >
                    <Twitter className="w-3.5 h-3.5" />
                    <span>Tweet on X</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleShare("pinterest")}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all text-xs text-gray-600"
                    aria-label="Share on Pinterest"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    <span>Pin it</span>
                  </motion.button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-t border-gray-200 pt-5">
                <div className="flex gap-3 mb-5 border-b border-gray-200 overflow-x-auto">
                  {[
                    { id: "description", label: "Description" },
                    { id: "dimensions", label: "Dimensions" },
                    { id: "care", label: "Care Tips" },
                    { id: "shipping", label: "Shipping" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`pb-2.5 px-3 text-sm font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab.id
                          ? "text-gray-900 border-b-2 border-gray-900"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="prose max-w-none"
                >
                  {activeTab === "description" && (
                    <div className="text-gray-600 leading-relaxed text-sm space-y-4">
                      <p>
                        {product.description || "No description available."}
                      </p>
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-2">Style Tip</h4>
                        <p className="text-gray-500">
                          Style this with the perfect wedding ceremony dress, or an ethnic piece specially made for big occasions!
                        </p>
                      </div>
                    </div>
                  )}
                  {activeTab === "dimensions" && (
                    <div className="text-gray-600 space-y-3 text-sm">
                      <div>
                        <strong className="text-gray-900 font-medium">Color:</strong>{" "}
                        <span className="text-gray-500">Ivory</span>
                      </div>
                      <div>
                        <strong className="text-gray-900 font-medium">Necklace:</strong>{" "}
                        <span className="text-gray-500">Length: 23*8.5 cm, Weight: 115 g</span>
                      </div>
                      <div>
                        <strong className="text-gray-900 font-medium">Earrings:</strong>{" "}
                        <span className="text-gray-500">Size: 6*3.5 cm, Weight of Pair: 38 g</span>
                      </div>
                      <div>
                        <strong className="text-gray-900 font-medium">Maang Tikka:</strong>{" "}
                        <span className="text-gray-500">Size: 16.5*3.5 cm, Weight: 22 g</span>
                      </div>
                      <div>
                        <strong className="text-gray-900 font-medium">Material:</strong>{" "}
                        <span className="text-gray-500">Alloy, Kundan, Pearls</span>
                      </div>
                      <div>
                        <strong className="text-gray-900 font-medium">Unit:</strong>{" "}
                        <span className="text-gray-500">One Necklace, One Pair Of Earrings, One Maang Tikka</span>
                      </div>
                    </div>
                  )}
                  {activeTab === "care" && (
                    <div className="text-gray-600 text-sm">
                      <h4 className="font-medium text-gray-900 mb-3">CARE TIPS</h4>
                      <ul className="list-disc list-inside space-y-1.5 text-gray-500">
                        <li>Store in a dry place away from moisture</li>
                        <li>Clean with a soft cloth after each use</li>
                        <li>Avoid contact with perfumes and chemicals</li>
                        <li>Keep in individual pouches to prevent scratches</li>
                        <li>Remove before swimming or showering</li>
                        <li>Polish gently with a jewelry cleaning cloth when needed</li>
                      </ul>
                    </div>
                  )}
                  {activeTab === "shipping" && (
                    <div className="text-gray-600 text-sm">
                      <h4 className="mb-3 font-medium text-gray-900">Shipping information</h4>
                      <ul className="list-disc list-inside space-y-1.5 text-gray-500">
                        <li>Free shipping on prepaid orders above ₹500</li>
                        <li>Express 24-hour delivery available in Mumbai (charges apply)</li>
                        <li>Standard delivery: 3-5 business days</li>
                        <li>COD available for orders above ₹500</li>
                        <li>International shipping available (charges apply)</li>
                        <li>All orders are carefully packaged to ensure safe delivery</li>
                      </ul>
                    </div>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>
      
      {/* OTP Login Dialog */}
      <OTPLoginDialog
        isOpen={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onSuccess={async () => {
          // After successful login, add to wishlist
          const authState = useAuthStore.getState();
          if (authState.token && product) {
            const success = await addItemToBackend(product.id, authState.token);
            if (success) {
              addToWishlist({
                id: product.id,
                name: product.name,
                price: product.price,
                originalPrice: product.original_price || undefined,
                image: product.image_url || getSmartProductPlaceholder(product.name),
                slug: product.slug,
              });
              toast.success("Added to wishlist!");
            }
          }
        }}
      />
    </div>
  );
}
